/**
 * Onboarding API Functions
 * 
 * All API calls for the onboarding module are centralized here.
 * Makes it easy to understand what endpoints exist and how to use them.
 */

import { API_URL } from './constants';

// Acting admin (R26). OnboardingApp sets this once the signed-in user is known;
// admin-gated calls append it as `user_id` so the server can check the caller
// is an owner/admin of the church. Explicit `userId` arguments still win.
let _actorId = null;
export function setOnboardingActor(userId) {
  _actorId = userId || null;
}
function actorParam(userId) {
  return userId || _actorId || null;
}
// Every admin call carries the acting user (`X-User-Id`) so the server can check
// the caller is an owner/admin of the church (ONB-Q4 closed 2026-09-06). Callers
// outside this file that fetch admin routes directly spread `actorHeaders()`.
export function actorHeaders() {
  return _actorId ? { 'X-User-Id': _actorId } : {};
}
function ofetch(url, init = {}) {
  return fetch(url, { ...init, headers: { ...actorHeaders(), ...(init.headers || {}) } });
}

// ==================== CANDIDATES ====================

/**
 * Fetch a single candidate (fresh data, incl. shared admin notes)
 */
export async function getCandidate(candidateId) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}`);
  if (!response.ok) throw new Error('Failed to fetch candidate');
  return response.json();
}

/** Add a note to a candidate's shared admin notes log */
export async function addCandidateNote(candidateId, { text, author_id, author_name }) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, author_id, author_name }),
  });
  if (!response.ok) throw new Error('Failed to add note');
  return response.json();
}

/** Edit a candidate note (any admin) */
export async function updateCandidateNote(candidateId, noteId, { text, editor_id, editor_name }) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/notes/${noteId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, editor_id, editor_name }),
  });
  if (!response.ok) throw new Error('Failed to update note');
  return response.json();
}

/** Delete a candidate note (any admin) */
export async function deleteCandidateNote(candidateId, noteId) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/notes/${noteId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete note');
  return response.json();
}

/**
 * Fetch all candidates for a church, grouped by stage
 */
export async function fetchCandidates(churchId, trackId = null) {
  const url = trackId
    ? `${API_URL}/api/onboarding/candidates/${churchId}?track_id=${encodeURIComponent(trackId)}`
    : `${API_URL}/api/onboarding/candidates/${churchId}`;
  const response = await ofetch(url);
  if (!response.ok) throw new Error('Failed to fetch candidates');
  return response.json();
}

// ==================== ALTERNATE ONBOARDING TRACKS (BETA) ====================

/** List a church's alternate tracks + per-track and Main candidate counts. */
export async function fetchTracks(churchId, includeArchived = false) {
  const url = `${API_URL}/api/onboarding/tracks/${churchId}${includeArchived ? '?include_archived=true' : ''}`;
  const response = await ofetch(url);
  if (!response.ok) throw new Error('Failed to fetch onboarding tracks');
  return response.json();
}

/** Create an alternate track. type = 'music' | 'other'. */
export async function createTrack(churchId, name, type) {
  const response = await ofetch(`${API_URL}/api/onboarding/tracks/${churchId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, type })
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to create track');
  }
  return response.json();
}

/** Update a track (name, labels, enabled_steps, content overrides). */
export async function updateTrack(churchId, trackId, updates) {
  const response = await ofetch(`${API_URL}/api/onboarding/tracks/${churchId}/${trackId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to update track');
  }
  return response.json();
}

export async function archiveTrack(churchId, trackId) {
  const response = await ofetch(`${API_URL}/api/onboarding/tracks/${churchId}/${trackId}/archive`, { method: 'POST' });
  if (!response.ok) throw new Error('Failed to archive track');
  return response.json();
}

export async function unarchiveTrack(churchId, trackId) {
  const response = await ofetch(`${API_URL}/api/onboarding/tracks/${churchId}/${trackId}/unarchive`, { method: 'POST' });
  if (!response.ok) throw new Error('Failed to unarchive track');
  return response.json();
}

/** Hard-delete a track (409 if it still has candidates — archive instead). */
export async function deleteTrack(churchId, trackId) {
  const response = await ofetch(`${API_URL}/api/onboarding/tracks/${churchId}/${trackId}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to delete track');
  }
  return response.json();
}

/** Replace an "other" track's positions list (syncs intake areas + training positions). */
export async function setTrackPositions(churchId, trackId, positions) {
  const response = await ofetch(`${API_URL}/api/onboarding/tracks/${churchId}/${trackId}/positions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ positions })
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to save positions');
  }
  return response.json();
}


/**
 * Move a candidate to a different stage
 */
export async function moveCandidate(candidateId, newStage) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/move`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stage: newStage })
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.detail || 'Failed to move candidate');
    err.status = response.status;
    throw err;
  }
  return response.json();
}

/**
 * Send audition prep materials email and move candidate to Auditioning stage.
 *
 * Single source of truth for "candidate enters Auditioning + gets the email":
 *   - Modal button ("Yes, send them audition prep material") calls this.
 *   - Drag-to-Auditioning interception in useOnboarding calls this.
 *
 * Backend endpoint atomically: sets stage='auditioned', sets previous_stage,
 * sets audition_materials_sent_at (powers the engagement panel), and fires
 * the email in a FastAPI BackgroundTask.
 */
export async function sendAuditionMaterials(candidateId) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/send-audition-materials`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to send audition materials');
  }
  return response.json();
}

/**
 * Update candidate details
 */
export async function updateCandidate(candidateId, updates) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
  if (!response.ok) throw new Error('Failed to update candidate');
  return response.json();
}

/**
 * Delete a candidate
 */
export async function deleteCandidate(candidateId) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Failed to delete candidate');
  return response.json();
}

/**
 * Snooze a candidate for a specified duration
 */
export async function snoozeCandidate(candidateId, days) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/snooze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ days })
  });
  if (!response.ok) throw new Error('Failed to snooze candidate');
  return response.json();
}

/**
 * Restore a snoozed candidate
 */
export async function restoreCandidate(candidateId) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/restore`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to restore candidate');
  return response.json();
}

// ==================== ENGAGEMENT — Resend / Reminder ====================
// (Phase 4 — Feb 2026)
// One-click email re-sends triggered from the engagement panel in the
// candidate detail modal. Each calls a thin backend endpoint that re-runs
// the same email-send helper used elsewhere, so engagement tags + tracking
// behave identically to the original send.

export async function resendAuditionMaterials(candidateId) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/resend-audition-materials`, {
    method: 'POST'
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to resend audition materials');
  }
  return response.json();
}

export async function resendOnboardingDocs(candidateId) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/resend-onboarding-docs`, {
    method: 'POST'
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to resend onboarding email');
  }
  return response.json();
}

export async function resendTraining(candidateId) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/resend-training`, {
    method: 'POST'
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to resend training email');
  }
  return response.json();
}

/**
 * Start training for a candidate with selected instrument
 */
export async function startTraining(candidateId, instruments) {
  const list = Array.isArray(instruments) ? instruments : [instruments];
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/start-training`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ instruments: list })
  });
  if (!response.ok) throw new Error('Failed to start training');
  return response.json();
}

// ==================== SETTINGS ====================

/**
 * Fetch onboarding settings for a church
 */
export async function fetchSettings(churchId, trackId = null) {
  const q = trackId ? `?track_id=${encodeURIComponent(trackId)}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/settings/${churchId}${q}`);
  if (!response.ok) throw new Error('Failed to fetch settings');
  return response.json();
}

// ==================== SNOOZED/ARCHIVED ====================

/**
 * Fetch snoozed candidates
 */
export async function fetchSnoozedCandidates(churchId) {
  const response = await ofetch(`${API_URL}/api/onboarding/snoozed/${churchId}`);
  if (!response.ok) throw new Error('Failed to fetch snoozed candidates');
  return response.json();
}

/**
 * Fetch archived candidates
 */
export async function fetchArchivedCandidates(churchId, trackId = null) {
  const q = trackId ? `?track_id=${encodeURIComponent(trackId)}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/archived/${churchId}${q}`);
  if (!response.ok) throw new Error('Failed to fetch archived candidates');
  return response.json();
}

/**
 * Archive a candidate (used after the on-team celebration)
 */
export async function archiveCandidate(candidateId) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/archive`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to archive candidate');
  return response.json();
}

/**
 * Restore an archived candidate into an admin-picked stage
 */
export async function unarchiveCandidate(candidateId, stage = null) {
  const response = await ofetch(`${API_URL}/api/onboarding/candidate/${candidateId}/unarchive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(stage ? { stage } : {})
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to restore candidate');
  }
  return response.json();
}

// ==================== CUSTOM POSITIONS ====================

/**
 * Create a custom training position
 */
export async function createCustomPosition(churchId, name, trackId = null) {
  const q = trackId ? `?track_id=${encodeURIComponent(trackId)}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/settings/${churchId}/custom-positions${q}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name })
  });
  if (!response.ok) throw new Error('Failed to create custom position');
  return response.json();
}

/**
 * Rename a custom training position
 */
export async function renameCustomPosition(churchId, positionId, name, trackId = null) {
  const q = trackId ? `?track_id=${encodeURIComponent(trackId)}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/settings/${churchId}/custom-positions/${positionId}${q}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name })
  });
  if (!response.ok) throw new Error('Failed to rename custom position');
  return response.json();
}

/**
 * Delete a custom training position
 */
export async function deleteCustomPosition(churchId, positionId, trackId = null) {
  const q = trackId ? `?track_id=${encodeURIComponent(trackId)}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/settings/${churchId}/custom-positions/${positionId}${q}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Failed to delete custom position');
  return response.json();
}

// ==================== TRAINING COVER THUMBNAILS ====================

/**
 * Upload a 16:9 cover thumbnail for a training position (built-in instrument
 * slug OR custom-position id). Returns { success, cover_image_url }.
 */
export async function uploadTrainingCover(churchId, instrumentKey, file, trackId = null) {
  const form = new FormData();
  form.append('file', file);
  const q = trackId ? `?track_id=${encodeURIComponent(trackId)}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/settings/${churchId}/training-cover/${instrumentKey}${q}`, {
    method: 'POST',
    body: form
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to upload cover image');
  }
  return response.json();
}

/**
 * Remove a training-position cover thumbnail (reverts to default/gradient).
 */
export async function deleteTrainingCover(churchId, instrumentKey, trackId = null) {
  const q = trackId ? `?track_id=${encodeURIComponent(trackId)}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/settings/${churchId}/training-cover/${instrumentKey}${q}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Failed to remove cover image');
  return response.json();
}

/**
 * Per-position "customized vs default" status for the Customize Training tabs.
 * Returns { customized: { <instrument_or_position_id>: bool } }.
 */
export async function getCustomizationStatus(churchId, trackId = null) {
  const q = trackId ? `?track_id=${encodeURIComponent(trackId)}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/settings/${churchId}/customization-status${q}`);
  if (!response.ok) throw new Error('Failed to load customization status');
  return response.json();
}

// ==================== TRAINING BLOCKS (unified block editor) ====================

/**
 * Fetch the unified, ordered block list for an instrument (church admin editor).
 * Returns { instrument, instrument_name, blocks: [...], migrated }.
 */
export async function fetchTrainingBlocks(churchId, instrument, trackId = null) {
  const q = trackId ? `?track_id=${encodeURIComponent(trackId)}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/training/blocks/${churchId}/${instrument}${q}`);
  if (!response.ok) throw new Error('Failed to fetch training blocks');
  return response.json();
}

/**
 * Save the block list for an instrument. The backend compiles blocks back into
 * `training_content` HTML and returns { success, compiled, migrated, block_count }.
 */
export async function saveTrainingBlocks(churchId, instrument, blocks, trackId = null, userId = null) {
  // userId: the acting admin — the server checks they are an owner/admin of churchId (R26).
  const params = new URLSearchParams();
  if (trackId) params.set('track_id', trackId);
  if (actorParam(userId)) params.set('user_id', actorParam(userId));
  const q = params.toString() ? `?${params.toString()}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/training/blocks/${churchId}/${instrument}${q}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blocks })
  });
  if (!response.ok) throw new Error('Failed to save training blocks');
  return response.json();
}

/**
 * Reset an instrument back to the global WorshipOS defaults.
 */
export async function resetTrainingBlocks(churchId, instrument, trackId = null, userId = null) {
  const params = new URLSearchParams();
  if (trackId) params.set('track_id', trackId);
  if (actorParam(userId)) params.set('user_id', actorParam(userId));
  const q = params.toString() ? `?${params.toString()}` : '';
  const response = await ofetch(`${API_URL}/api/onboarding/training/blocks/${churchId}/${instrument}${q}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Failed to reset training blocks');
  return response.json();
}

// ==================== SUPER-ADMIN: PUBLISH DEFAULTS TO ALL CHURCHES ====================

/**
 * Dry-run preview for "Publish to all churches" (super-admin only). Returns
 * { churches_total, churches_affected, new_blocks_total, churches_losing_content }.
 * Writes nothing.
 */
export async function getPublishPreview(userEmail, churchId) {
  const response = await ofetch(
    `${API_URL}/api/onboarding/admin/publish-training-preview`
    + `?user_email=${encodeURIComponent(userEmail || '')}&church_id=${encodeURIComponent(churchId || '')}`
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to load publish preview');
  }
  return response.json();
}

/**
 * Publish the super-admin account's current training as the global master AND
 * non-destructively merge it into every church (adds new default blocks, never
 * erases custom work, honors per-church tombstones).
 */
export async function publishTrainingToAll(userEmail, churchId) {
  const response = await ofetch(`${API_URL}/api/onboarding/admin/publish-training`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_email: userEmail, church_id: churchId })
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || 'Failed to publish training');
  }
  return response.json();
}
